import React from 'react';
import { BarcodeDetector } from '../../../../lib/inventoryBarcodeDetector';
import { acquireCameraSession } from '../../../../lib/cameraSession';
import './picking.css';

const CAMERA_FORMATS: string[] = [
  'code_128', 'code_39', 'code_93', 'codabar',
  'ean_13', 'ean_8', 'itf', 'qr_code', 'upc_a', 'upc_e',
  'data_matrix', 'aztec', 'pdf417',
];

export interface ScanFeedback {
  success: boolean;
  message: string;
}

interface PickingScanInputProps {
  /**
   * Called when a barcode is scanned. Returns feedback to display in-camera.
   * If the function returns void/undefined, no in-camera toast is shown.
   */
  onScan: (barcode: string, source: 'camera' | 'bluetooth' | 'manual') => void | Promise<void | ScanFeedback>;
  disabled?: boolean;
  autoStartCamera?: boolean;
  /** Single lookup starts paused after a detection; warehouse defaults stay continuous. */
  initialAutoNext?: boolean;
  /** Visible label above the field. Without it the field has no label, as before. */
  label?: string;
  placeholder?: string;
  /**
   * Shows "Enter Barcode" and its manual-entry dialog (default). Typing a code into the field and
   * pressing Enter works either way.
   */
  showManualEntry?: boolean;
}

type CameraToast = {
  id: number;
  success: boolean;
  message: string;
};

let toastIdCounter = 0;

const PickingScanInput: React.FC<PickingScanInputProps> = ({
  onScan,
  disabled,
  autoStartCamera = false,
  initialAutoNext = true,
  label,
  placeholder = 'Scan barcode or type asset ID...',
  showManualEntry = true,
}) => {
  const inputId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const releaseCameraRef = React.useRef<(() => void) | null>(null);
  const frameRef = React.useRef<number>(0);
  const scanningRef = React.useRef(false);
  const autoNextRef = React.useRef(false);

  const [bluetoothValue, setBluetoothValue] = React.useState('');
  const [cameraOpen, setCameraOpen] = React.useState(autoStartCamera);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [manualOpen, setManualOpen] = React.useState(false);
  const [manualValue, setManualValue] = React.useState('');
  const [lastScan, setLastScan] = React.useState<{ barcode: string; time: number } | null>(null);
  const [autoNext, setAutoNext] = React.useState(initialAutoNext);
  const [cameraToasts, setCameraToasts] = React.useState<CameraToast[]>([]);
  const [scanCount, setScanCount] = React.useState(0);

  const lastScannedRef = React.useRef<string | null>(null);
  const lastScanTimeRef = React.useRef<number>(0);
  const onScanRef = React.useRef(onScan);
  const disabledRef = React.useRef(disabled);
  React.useEffect(() => { onScanRef.current = onScan; disabledRef.current = disabled; }, [onScan, disabled]);

  // Keep autoNext ref in sync so the scan loop sees the latest value
  React.useEffect(() => {
    autoNextRef.current = autoNext;
  }, [autoNext]);

  React.useEffect(() => {
    if (autoStartCamera) {
      setCameraOpen(true);
    }
  }, [autoStartCamera]);

  // Auto-focus the bluetooth input when camera is closed
  React.useEffect(() => {
    if (!cameraOpen && !manualOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [cameraOpen, manualOpen]);

  React.useEffect(() => {
    if (!cameraOpen || typeof document === 'undefined') return;
    if (inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.blur();
    }
  }, [cameraOpen]);

  const showCameraToast = React.useCallback((success: boolean, message: string) => {
    const id = ++toastIdCounter;
    setCameraToasts((prev) => [...prev.slice(-4), { id, success, message }]);
    setTimeout(() => {
      setCameraToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2500);
  }, []);

  const stopCamera = React.useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }
    if (releaseCameraRef.current) {
      releaseCameraRef.current();
      releaseCameraRef.current = null;
    }
    streamRef.current = null;
  }, []);

  // Cleanup on unmount
  React.useEffect(() => stopCamera, [stopCamera]);

  // Process a scanned barcode and handle feedback
  const processScan = React.useCallback(
    async (value: string, source: 'camera' | 'bluetooth' | 'manual') => {
      setLastScan({ barcode: value, time: Date.now() });
      setScanCount((c) => c + 1);
      try {
        if (disabledRef.current) return;
        const result = await onScanRef.current(value, source);
        if (result && typeof result === 'object' && 'success' in result) {
          if (cameraOpen) {
            showCameraToast(result.success, result.message);
          }
          return result;
        }
      } catch (err: any) {
        if (cameraOpen) {
          showCameraToast(false, err?.message ?? 'Scan error');
        }
      }
      return undefined;
    },
    [cameraOpen, showCameraToast],
  );

  // Bluetooth scanner input handler (Enter to submit)
  const handleBluetoothKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && bluetoothValue.trim()) {
        const val = bluetoothValue.trim();
        const now = Date.now();
        if (val === lastScannedRef.current && now - lastScanTimeRef.current < 1000) {
          setBluetoothValue('');
          return;
        }
        lastScannedRef.current = val;
        lastScanTimeRef.current = now;
        setBluetoothValue('');
        processScan(val, 'bluetooth');
      }
    },
    [bluetoothValue, processScan],
  );

  // Manual barcode submit
  const handleManualSubmit = React.useCallback(() => {
    const val = manualValue.trim();
    if (!val) return;
    setManualOpen(false);
    setManualValue('');
    processScan(val, 'manual');
  }, [manualValue, processScan]);

  // Camera scanning effect with continuous / auto-next support
  React.useEffect(() => {
    if (!cameraOpen) {
      stopCamera();
      setCameraError(null);
      return;
    }
    if (typeof window === 'undefined') return;

    let active = true;
    let detector: BarcodeDetector | null = null;
    setCameraError(null);

    const setup = async () => {
      try {
        detector = new BarcodeDetector({ formats: CAMERA_FORMATS as any });
        const session = await acquireCameraSession({
          facingMode: 'environment',
          widthIdeal: 1280,
          heightIdeal: 720,
        });
        const stream = session.stream;
        if (!active) {
          session.release();
          return;
        }
        releaseCameraRef.current = session.release;
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const DEBOUNCE_MS = 800;

        const scan = async () => {
          if (!active) return;

          // Don't scan while processing a previous scan
          if (scanningRef.current || disabledRef.current) {
            frameRef.current = requestAnimationFrame(scan);
            return;
          }

          try {
            if (videoRef.current && detector) {
              const codes = await detector.detect(videoRef.current);
              if (codes?.length) {
                const value = String(codes[0]?.rawValue ?? '').trim();
                if (value) {
                  const now = Date.now();
                  // Debounce: don't re-scan the same code within DEBOUNCE_MS
                  if (value === lastScannedRef.current && now - lastScanTimeRef.current < DEBOUNCE_MS) {
                    frameRef.current = requestAnimationFrame(scan);
                    return;
                  }
                  lastScannedRef.current = value;
                  lastScanTimeRef.current = now;

                  scanningRef.current = true;
                  try {
                    const feedback = await processScan(value, 'camera');

                    // If auto-next is OFF, close camera after successful scan
                    if (!autoNextRef.current) {
                      if (!active) return;
                      setCameraOpen(false);
                      stopCamera();
                      return;
                    }
                    // With auto-next ON, keep scanning
                  } finally {
                    scanningRef.current = false;
                  }
                }
              }
            }
          } catch {
            if (!active) return;
            active = false;
            stopCamera();
            setCameraError('Camera scanning stopped because the barcode reader is unavailable. Close and reopen Camera to retry, or type a barcode or use a Bluetooth scanner.');
          }
          if (active) {
            frameRef.current = requestAnimationFrame(scan);
          }
        };
        frameRef.current = requestAnimationFrame(scan);
      } catch (err: any) {
        if (!active) return;
        active = false;
        stopCamera();
        setCameraError(`Camera could not start. ${err?.message || 'Check camera access.'} You can still type a barcode or use a Bluetooth scanner.`);
      }
    };

    setup();
    return () => {
      active = false;
      stopCamera();
    };
    // processScan is intentionally omitted to avoid restarting the camera on every scan
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen, stopCamera]);

  return (
    <div className="picking-scan-input">
      {label ? (
        <label className="picking-scan-input__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className="picking-scan-input__row">
        <input
          ref={inputRef}
          id={label ? inputId : undefined}
          type="text"
          className="picking-scan-input__field"
          placeholder={placeholder}
          value={bluetoothValue}
          onChange={(e) => setBluetoothValue(e.target.value)}
          onKeyDown={handleBluetoothKeyDown}
          disabled={disabled}
          autoFocus={!cameraOpen}
        />
        <button
          type="button"
          className={`btn btn--sm ${cameraOpen ? 'btn--primary' : ''} picking-scan-input__camera-btn`}
          onClick={() => setCameraOpen((o) => !o)}
          title={cameraOpen ? 'Close camera' : 'Open camera scanner'}
          disabled={disabled}
        >
          {cameraOpen ? 'Close Camera' : 'Camera'}
        </button>
        {showManualEntry ? (
          <button
            type="button"
            className="btn btn--sm picking-scan-input__manual-btn"
            onClick={() => setManualOpen(true)}
            disabled={disabled}
          >
            Enter Barcode
          </button>
        ) : null}
      </div>

      {/* Auto-next and last scan info */}
      <div className="picking-scan-input__status-row">
        <label className="picking-scan-input__auto-next">
          <input
            type="checkbox"
            checked={autoNext}
            onChange={(e) => setAutoNext(e.target.checked)}
          />
          <span>Auto next scan</span>
        </label>
        {lastScan && (
          <span className="picking-scan-input__last-scan">
            Last: <strong>{lastScan.barcode}</strong>
            {scanCount > 1 && (
              <span className="picking-scan-input__scan-count">
                ({scanCount} scanned)
              </span>
            )}
          </span>
        )}
      </div>

      {/* Camera viewfinder with crosshair and toasts */}
      {cameraOpen && (
        <div className="picking-scan-input__camera">
          {cameraError ? (
            <div className="picking-scan-input__camera-error">{cameraError}</div>
          ) : (
            <div className="picking-scan-input__viewfinder">
              <video
                ref={videoRef}
                className="picking-scan-input__video"
                playsInline
                muted
              />

              {/* Crosshair overlay */}
              <div className="picking-scan-input__crosshair">
                <div className="picking-scan-input__crosshair-box">
                  <div className="picking-scan-input__crosshair-corner picking-scan-input__crosshair-corner--tl" />
                  <div className="picking-scan-input__crosshair-corner picking-scan-input__crosshair-corner--tr" />
                  <div className="picking-scan-input__crosshair-corner picking-scan-input__crosshair-corner--bl" />
                  <div className="picking-scan-input__crosshair-corner picking-scan-input__crosshair-corner--br" />
                  <div className="picking-scan-input__crosshair-line" />
                </div>
                <div className="picking-scan-input__crosshair-label">
                  Point at barcode or QR code
                </div>
              </div>

              {/* In-camera scan toasts */}
              <div className="picking-scan-input__camera-toasts">
                {cameraToasts.map((toast) => (
                  <div
                    key={toast.id}
                    className={`picking-scan-input__camera-toast ${
                      toast.success
                        ? 'picking-scan-input__camera-toast--success'
                        : 'picking-scan-input__camera-toast--error'
                    }`}
                  >
                    <span className="picking-scan-input__camera-toast-icon">
                      {toast.success ? '\u2713' : '\u2717'}
                    </span>
                    <span className="picking-scan-input__camera-toast-msg">
                      {toast.message}
                    </span>
                  </div>
                ))}
              </div>

              {/* Auto-next indicator badge */}
              {autoNext && (
                <div className="picking-scan-input__auto-badge">
                  AUTO
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manual entry dialog */}
      {showManualEntry && manualOpen && (
        <div className="picking-scan-input__manual-dialog">
          <div className="picking-scan-input__manual-backdrop" />
          <div className="picking-scan-input__manual-content">
            <h4>Enter Barcode Manually</h4>
            <input
              type="text"
              className="picking-scan-input__field"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleManualSubmit();
                if (e.key === 'Escape') {
                  setManualOpen(false);
                  setManualValue('');
                }
              }}
              placeholder="Type barcode..."
              autoFocus
            />
            <div className="picking-scan-input__manual-actions">
              <button type="button" className="btn btn--sm btn--primary" onClick={handleManualSubmit}>
                Submit
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => { setManualOpen(false); setManualValue(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickingScanInput;
